import type { Money } from '@pf/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, symbolFor, type FormatMoneyOptions } from '@/lib/money';
import { cn } from '@/lib/utils';

/**
 * Renders a monetary amount.
 *
 * The one rule: **the amount is derived from the decimal string and nothing else.** No
 * `parseFloat`, no `Number()`, no `Intl.NumberFormat` (which takes a `number` and would round
 * silently at 2^53). `lib/money.ts` does the grouping on `BigInt`, so what is displayed is
 * always byte-exact with what the API sent — see `docs/plan/06-risks.md` R3 for why this is a
 * lint-enforced rule rather than a preference.
 *
 * Amounts also render with tabular figures so columns of them align, and the currency is always
 * present — either as a symbol or an ISO code — because "500" with no unit is not a number an
 * operator should ever have to interpret.
 */

/** Props for {@link MoneyDisplay}. */
export interface MoneyDisplayProps extends FormatMoneyOptions {
  /** The amount, as the contract carries it. */
  amount: Money;
  /** Visual weight. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | undefined;
  /** Render the currency symbol at reduced emphasis. Defaults to `true`. */
  mutedSymbol?: boolean | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

const SIZES = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg font-semibold',
  xl: 'text-3xl font-semibold tracking-tight',
} as const;

/** Display a {@link Money} exactly. */
export function MoneyDisplay({
  amount,
  size = 'md',
  mutedSymbol = true,
  className,
  ...format
}: MoneyDisplayProps): ReactNode {
  const body = formatMoney(amount, format);
  const symbol = symbolFor(amount.currency);
  return (
    <span
      className={cn(
        'tnum inline-flex items-baseline gap-1 whitespace-nowrap',
        SIZES[size],
        className,
      )}
      title={`${amount.value} ${amount.currency}`}
    >
      <span
        className={cn('font-medium', mutedSymbol && 'text-muted-foreground')}
        aria-hidden="true"
      >
        {symbol}
      </span>
      <span>{body}</span>
      <span className="sr-only">{` ${amount.currency}`}</span>
    </span>
  );
}

/** Props for {@link MoneyDelta}. */
export interface MoneyDeltaProps {
  /** The amount on the slip or the actual value. */
  actual: Money;
  /** The reserved or expected value. */
  expected: Money;
  /** Additional classes. */
  className?: string | undefined;
}

/**
 * Show an actual amount against the expected one, calling out any difference.
 *
 * Used on the Manual Review screen, where "the slip says 480 but the reservation was 500" is the
 * single most important fact on the page. Comparison is exact string comparison — an amount that
 * differs in the fourth decimal place is still a mismatch.
 */
export function MoneyDelta({ actual, expected, className }: MoneyDeltaProps): ReactNode {
  const { t } = useTranslation();
  const same = actual.value === expected.value && actual.currency === expected.currency;
  return (
    <span className={cn('inline-flex items-baseline gap-2', className)}>
      <MoneyDisplay amount={actual} className={same ? undefined : 'text-state-bad font-semibold'} />
      {same ? null : (
        <span className="text-xs text-muted-foreground">
          {t('domain.moneyDisplay.expectedLabel')} <MoneyDisplay amount={expected} size="sm" />
        </span>
      )}
    </span>
  );
}
